import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Search, Download, RefreshCw, ChevronLeft, ChevronRight, X, } from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import TransactionTable from "@/shared/components/transactions/TransactionTable";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import * as transactionService from "@/shared/api/services/transactionService";
import * as resellerService from "@/shared/api/services/resellerService";
import { mapGlobalTransactions } from "@/shared/utils/transactionMappers";

export default function ResellerWithdrawals() {
  const { t, language } = useLanguage();

  // Internal input state for smooth typing
  const [inputValue, setInputValue] = useState("");
  // Search query state that triggers API
  const [searchQuery, setSearchQuery] = useState("");

  const [statusFilter, setStatusFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [selectedTx, setSelectedTx] = useState(null);
  const [page, setPage] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pageSize = 10;

  const resellerName = "Reseller Portal";

  // Handle debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(inputValue);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Sync inputValue if search is cleared externally
  useEffect(() => {
    if (searchQuery === "") setInputValue("");
  }, [searchQuery]);

  // Fetch merchants for this reseller
  const { data: merchants = [], isLoading: merchantsLoading } = useQuery({
    queryKey: ["reseller-merchants"],
    queryFn: resellerService.getMyMerchants,
  });

  const merchantIds = useMemo(() => {
    return merchants.map(m => m.id);
  }, [merchants]);

  const { data: paginationResult, isLoading: txLoading, refetch: refetchTransactions } = useQuery({
    queryKey: ["reseller-withdrawals", page, pageSize, searchQuery, statusFilter, currencyFilter, merchantIds],
    queryFn: () => {
      const backendFilters = { type: 'withdrawal' };
      if (statusFilter !== "all") backendFilters.status = statusFilter;
      if (currencyFilter !== "all") backendFilters.currency = currencyFilter;

      return transactionService.getPaginatedTransactions(
        page + 1,
        pageSize,
        searchQuery,
        backendFilters,
        merchantIds.length > 0 ? merchantIds : ["_none_"]
      );
    },
    enabled: !merchantsLoading,
    keepPreviousData: true,
  });

  const rawTransactions = paginationResult?.data || [];
  const totalTransactions = paginationResult?.total || 0;
  const totalPages = paginationResult?.totalPages || 1;

  const transactions = useMemo(() => {
    return mapGlobalTransactions(rawTransactions);
  }, [rawTransactions]);



  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetchTransactions();
    } finally {
      setIsRefreshing(false);
    }
  };


  const isLoading = merchantsLoading || txLoading;

  return (
    <ResellerLayout currentPage="ResellerWithdrawals" resellerName={resellerName}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t('withdrawals')}</h1>
            <p className="text-slate-500">{totalTransactions.toLocaleString()} {t("transactions")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={handleRefresh} disabled={isRefreshing} className="bg-white">
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            </Button>
            <Button variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              {t('export')}
            </Button>


          </div>
        </div>



        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t('search') + "..."}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={currencyFilter} onValueChange={(v) => { setCurrencyFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder={t('currency')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allCurrencies') || 'All Currencies'}</SelectItem>
                <SelectItem value="PKR">Thai (RS PKR)</SelectItem>
                <SelectItem value="IDR">Indonesian (Rp IDR)</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t('status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allStatus')}</SelectItem>
                <SelectItem value="success">{t('success')}</SelectItem>
                <SelectItem value="pending">{t('pending')}</SelectItem>
                <SelectItem value="failed">{t('failed')}</SelectItem>
              </SelectContent>
            </Select>

            {(inputValue || statusFilter !== "all" || currencyFilter !== "all") && (
              <Button variant="ghost" onClick={() => { setInputValue(""); setSearchQuery(""); setStatusFilter("all"); setCurrencyFilter("all"); setPage(0); }} className="text-slate-500 gap-2 h-10 hover:bg-slate-50">
                <X className="h-4 w-4" />
                <span className="text-sm font-medium">{t("clearFilters") || "Clear"}</span>
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        <TransactionTable
          transactions={transactions}
          isLoading={isLoading}
          onView={setSelectedTx}
          mixedTypes={false}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-slate-500">
              {t("showing")} {page * pageSize + 1} {t("to")} {Math.min((page + 1) * pageSize, totalTransactions)} {t("of")} {totalTransactions.toLocaleString()} {t("transactions")}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600">{t("page")} {page + 1} {t("of")} {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <TransactionDetailPanel transaction={selectedTx} open={!!selectedTx} onClose={() => setSelectedTx(null)} />
      </div>
    </ResellerLayout>
  );
}